import { useEffect, useState } from "react"
import Drawer from "@mui/material/Drawer"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import Divider from "@mui/material/Divider"
import { Iconify } from "@/lib/iconify"
import { store } from "@/blyn/legacy/state/store"
import { HELP, DEFAULT_HELP } from "@/blyn/legacy/components/helpDrawer"

const HOST_HEADER_TOP = {
  xs: "var(--layout-header-mobile-height, 0px)",
  lg: "var(--layout-header-desktop-height, 0px)",
} as const

const HOST_HEADER_HEIGHT = {
  xs: "calc(100% - var(--layout-header-mobile-height, 0px))",
  lg: "calc(100% - var(--layout-header-desktop-height, 0px))",
} as const

const RELATED_LINKS = [
  { id: "staged", label: "Staged actions" },
  { id: "conversation", label: "Conversations" },
  { id: "forecast", label: "Insights" },
  { id: "nudges", label: "Nudges" },
]

export function HelpDrawer() {
  const [open, setOpen] = useState(false)
  const [viewId, setViewId] = useState<string | null>(null)

  useEffect(() => {
    const unsubOpen = store.select("helpOpen", (v: boolean) => setOpen(!!v))
    const unsubViewId = store.select("helpViewId", (v: string | null) => setViewId(v))
    return () => { unsubOpen(); unsubViewId() }
  }, [])

  const close = () => store.set({ helpOpen: false })
  const help = (viewId && (HELP as Record<string, typeof DEFAULT_HELP>)[viewId]) || DEFAULT_HELP

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      hideBackdrop
      PaperProps={{
        sx: (theme) => ({
          width: 360,
          top: HOST_HEADER_TOP,
          height: HOST_HEADER_HEIGHT,
          borderRadius: 0,
          bgcolor: theme.palette.background.paper,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", p: "16px 16px 12px", borderBottom: "1px solid var(--border-subtle)", flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 0.5 }}>
            HELP · {(viewId || "").toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.3 }}>
            {help.title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={close} sx={{ mt: -0.5, color: "var(--text-tertiary)", "&:hover": { color: "var(--text-primary)" } }}>
          <Iconify icon="solar:close-circle-bold" width={18} />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {/* Intro */}
        <Typography sx={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, mb: 2.5 }}>
          {help.intro}
        </Typography>

        {/* Tasks */}
        {help.tasks?.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 1.5 }}>
              COMMON TASKS
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {help.tasks.map((task, i) => (
                <Box key={i} sx={{ bgcolor: "var(--bg-raised)", borderRadius: "var(--r-md)", p: 1.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", mb: 1 }}>
                    {task.title}
                  </Typography>
                  <Box sx={{ bgcolor: "var(--bg-app)", borderRadius: "var(--r-sm)", p: 1.25, mb: 1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 0.5 }}>
                      EXAMPLE
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                      {task.example}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                    <Box component="span" sx={{ color: "var(--success-text)" }}>→</Box>{" "}{task.outcome}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Concepts */}
        {help.concepts?.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 1.5 }}>
              KEY CONCEPTS
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {help.concepts.map((concept, i) => (
                <Box key={i}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", mb: 0.25 }}>
                    {concept.term}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                    {concept.def}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Shortcuts */}
        {help.shortcuts?.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 1.5 }}>
              KEYBOARD SHORTCUTS
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {help.shortcuts.map(([key, label], i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="kbd" sx={{ fontFamily: "monospace", fontSize: 11, px: 0.75, py: 0.25, bgcolor: "var(--bg-raised)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-sm)", color: "var(--text-primary)", flexShrink: 0 }}>
                    {key}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Related */}
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ borderColor: "var(--border-subtle)", mb: 1.5 }} />
          <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "var(--text-quaternary)", textTransform: "uppercase", mb: 1 }}>
            RELATED
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {RELATED_LINKS.map(({ id, label }) => (
              <Box
                key={id}
                component="button"
                onClick={() => store.set({ helpViewId: id })}
                sx={{ background: "none", border: "none", cursor: "pointer", p: 0, fontSize: 12, color: "var(--accent-text)", textDecoration: "underline", "&:hover": { color: "var(--text-primary)" } }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Typography sx={{ fontSize: 11, color: "var(--text-quaternary)", lineHeight: 1.8 }}>
          Press{" "}
          <Box component="kbd" sx={{ fontFamily: "monospace", px: 0.5, py: 0.125, bgcolor: "var(--bg-raised)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-sm)", fontSize: 11 }}>
            ESC
          </Box>
          {" "}to close · Still stuck?{" "}
          <Box component="a" href="mailto:support@boostt.ai" sx={{ color: "var(--accent-text)" }}>
            support@boostt.ai
          </Box>
        </Typography>
      </Box>
    </Drawer>
  )
}
